import type { Terminal } from "../terminal/terminal.ts";

/* ───────────────────────────────────────────────────────────────
   The content pane — a second tile beside the terminal, for content
   that doesn't belong in a scrollback (blog posts, long documents).

   On wide screens the terminal docks to a left column and the pane
   fills the rest, tiling-WM style. On compact screens the pane takes
   over the viewport and the terminal steps aside (see the max-width
   block in styles.css).

   The pane owns no routing itself — router.ts drives it, so a pane is
   always reachable by URL. Call open()/close() through the router,
   not directly, or the address bar will drift out of sync.
   ─────────────────────────────────────────────────────────────── */

export interface PaneContent {
  /** Shown in the pane's title bar. */
  title: string;
  /** Trusted HTML for the body. Generated by us at build time, never
      user input — see scripts/build-blog.ts. */
  html: string;
}

export class Pane {
  private root: HTMLElement;
  private titleEl: HTMLElement;
  private bodyEl: HTMLElement;
  private terminal: Terminal;
  private onClose: () => void;

  /** Currently open content key (e.g. a post slug), or null when closed. */
  private currentKey: string | null = null;

  constructor(mount: HTMLElement, terminal: Terminal, onClose: () => void) {
    this.terminal = terminal;
    this.onClose = onClose;

    this.root = document.createElement("aside");
    this.root.className = "pane";
    this.root.hidden = true;
    this.root.setAttribute("aria-label", "Content pane");

    const bar = document.createElement("div");
    bar.className = "pane-bar";

    this.titleEl = document.createElement("div");
    this.titleEl.className = "pane-title";

    const closeBtn = document.createElement("button");
    closeBtn.className = "pane-close";
    closeBtn.type = "button";
    closeBtn.textContent = "esc ✕";
    closeBtn.setAttribute("aria-label", "Close pane");
    closeBtn.addEventListener("click", () => this.onClose());

    bar.append(this.titleEl, closeBtn);

    this.bodyEl = document.createElement("div");
    this.bodyEl.className = "pane-body";
    this.bodyEl.tabIndex = 0; // keyboard-scrollable

    this.root.append(bar, this.bodyEl);
    mount.append(this.root);

    // Esc closes, matching the button's label — but not while the user is
    // mid-command in the terminal input.
    addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !this.isOpen) return;
      if (document.activeElement === document.querySelector(".cmdline")) return;
      this.onClose();
    });
  }

  get isOpen(): boolean {
    return this.currentKey !== null;
  }

  get key(): string | null {
    return this.currentKey;
  }

  open(key: string, content: PaneContent): void {
    this.currentKey = key;
    this.titleEl.textContent = content.title;
    this.bodyEl.innerHTML = content.html;
    this.bodyEl.scrollTop = 0;
    this.root.hidden = false;
    document.body.classList.add("paned");
    this.terminal.setDocked(true);
    this.bodyEl.focus({ preventScroll: true });
  }

  /** Replace the body with a message (loading, error) without changing key. */
  setMessage(title: string, message: string): void {
    this.titleEl.textContent = title;
    this.bodyEl.innerHTML = `<div class="prose"><p>${escapeHtml(message)}</p></div>`;
  }

  close(): void {
    this.currentKey = null;
    this.root.hidden = true;
    this.bodyEl.replaceChildren();
    document.body.classList.remove("paned");
    this.terminal.setDocked(false);
    this.terminal.focus();
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
